import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Snowflake } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useWalletListQuery,
  useWalletPayoutConfigQuery,
  useWalletPayoutsQuery,
} from '@/client/api/payment/payment.query';
import { useWalletPayoutActionMutation } from '@/client/api/payment/payment.mutation';
import { useFeatureFlagListQuery } from '@/client/api/feature-flags/feature-flags.query';
import { useUpdateFeatureFlagMutation } from '@/client/api/feature-flags/feature-flags.mutation';
import { flagToEntry } from '@/client/components/admin/FeatureFlags/utils';
import {
  formatMoney,
  Wallet,
  WalletListParams,
  WalletPayout,
  WalletPayoutAction,
} from '@/client/types/payment';
import {
  OrgRelationshipLink,
  StoreRelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';
import Pagination from '@/client/components/common/Pagination';

const LIMIT = 25;

const fmtDate = (raw?: string | null) =>
  raw
    ? new Date(raw).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const selectCls = 'rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm';
const thCls = 'px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500';
const card = 'overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm';

type OwnerRow = Pick<Wallet, 'uid' | 'oid' | 'user_summary' | 'org_summary'> &
  Pick<WalletPayout, 'storeid' | 'store_summary'>;

const Owner = ({ row }: { row: OwnerRow }) =>
  row.storeid ? (
    <StoreRelationshipLink storeid={row.storeid} summary={row.store_summary} />
  ) : row.oid ? (
    <OrgRelationshipLink oid={row.oid} summary={row.org_summary} />
  ) : row.uid ? (
    <UserRelationshipLink uid={row.uid} summary={row.user_summary} />
  ) : (
    <span className="text-gray-400">—</span>
  );

const SkeletonRows = ({ cols }: { cols: number }) => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <tr key={i}>
        {Array.from({ length: cols }).map((__, j) => (
          <td key={j} className="px-5 py-3">
            <Skeleton className="h-4 w-16" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// ─── Browse all wallets ───────────────────────────────────────────────────────

export const WalletBrowse = ({ onOpen }: { onOpen: (wid: number) => void }) => {
  const [filters, setFilters] = useState<WalletListParams>({});
  const [page, setPage] = useState(1);
  const { data, isLoading } = useWalletListQuery({
    ...filters,
    limit: LIMIT,
    offset: (page - 1) * LIMIT,
  });

  const set = (patch: WalletListParams) => {
    setPage(1);
    setFilters((f) => ({ ...f, ...patch }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(data?.summary ?? []).map((s) => (
          <div key={s.ccode} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <p className="text-xs text-gray-500">Held in {s.ccode} wallets</p>
            <p className="mt-1 text-2xl font-black tabular-nums text-gray-900">
              {formatMoney(s.balance, s.ccode)}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {s.wallets.toLocaleString()} wallets · {s.frozen.toLocaleString()} frozen
            </p>
          </div>
        ))}
      </div>

      <div className={card}>
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <h2 className="mr-auto text-sm font-semibold text-gray-800">All wallets</h2>
          <select
            aria-label="Owner type"
            className={selectCls}
            value={filters.owner ?? ''}
            onChange={(e) =>
              set({
                owner: (e.target.value || undefined) as WalletListParams['owner'],
              })
            }
          >
            <option value="">Users and organizations</option>
            <option value="user">Users</option>
            <option value="org">Organizations</option>
          </select>
          <select
            aria-label="Frozen state"
            className={selectCls}
            value={filters.frozen ?? ''}
            onChange={(e) =>
              set({
                frozen: e.target.value === '' ? undefined : (Number(e.target.value) as 0 | 1),
              })
            }
          >
            <option value="">Any state</option>
            <option value="1">Frozen</option>
            <option value="0">Not frozen</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={!!filters.funded}
              onChange={(e) => set({ funded: e.target.checked ? 1 : undefined })}
            />
            With a balance
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="bg-gray-50">
                {['WID', 'Owner', 'Balance', 'State', 'Created'].map((h) => (
                  <th key={h} className={thCls}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {isLoading && <SkeletonRows cols={5} />}
              {!isLoading && !data?.wallets?.length && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-sm text-gray-500">
                    No wallets match these filters
                  </td>
                </tr>
              )}
              {!isLoading &&
                data?.wallets?.map((w) => (
                  <tr key={w.wid} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        className="font-mono text-sm text-blue-600 hover:underline"
                        onClick={() => onOpen(w.wid)}
                      >
                        {w.wid}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-sm">
                      <Owner row={w} />
                    </td>
                    <td className="px-5 py-3 text-sm font-semibold tabular-nums">
                      {formatMoney(w.balance, w.ccode)}
                    </td>
                    <td className="px-5 py-3 text-sm">
                      {w.hold ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                          <Snowflake className="h-3 w-3" /> Frozen
                        </span>
                      ) : (
                        <span className="text-gray-500">Active</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-500">
                      {fmtDate(w.date_added as string | undefined)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-100 px-5 py-3">
          <Pagination
            total={data?.pagination?.total ?? 0}
            page={page}
            itemsPerPage={LIMIT}
            limit={LIMIT}
            setPage={setPage}
            showButtons
            showSelect={false}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Where a wallet's withdrawals go ──────────────────────────────────────────

export const WalletPayoutConfigCard = ({ wid }: { wid: number }) => {
  const { data: cfg, isLoading } = useWalletPayoutConfigQuery(wid);
  const rows: Array<[string, string | number | null | undefined]> = cfg
    ? [
        ['Method', cfg.provider],
        ['Minimum', cfg.min_payout_amount],
        ['Bank', cfg.bank_name],
        ['Account number', cfg.account_number],
        ['Account name', cfg.account_name],
        ['MoMo number', cfg.momo_msisdn],
        ['MoMo network', cfg.momo_network],
        ['Paystack recipient', cfg.paystack_recipient_code],
      ]
    : [];
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold text-gray-800">Payout details</h2>
      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : !cfg ? (
        <p className="text-sm text-gray-500">
          The owner has not set payout details, so they cannot withdraw yet.
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          {rows
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs text-gray-500">{k}</dt>
                <dd className="font-medium text-gray-900">{String(v)}</dd>
              </div>
            ))}
        </dl>
      )}
    </div>
  );
};

// ─── Withdrawals ──────────────────────────────────────────────────────────────

const STATUS_CLS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  processing: 'bg-blue-50 text-blue-700',
  completed: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

const ACTION_COPY: Record<WalletPayoutAction, { label: string; title: string; body: string }> = {
  retry: {
    label: 'Send',
    title: 'Send this withdrawal to the provider?',
    body: 'Dispatches the transfer through the provider on the withdrawal. Manual withdrawals move to processing for you to pay out by hand.',
  },
  complete: {
    label: 'Mark paid',
    title: 'Mark this withdrawal as paid?',
    body: 'Only confirm once the money has actually reached the owner. The held amount is not returned to the wallet.',
  },
  fail: {
    label: 'Fail',
    title: 'Fail this withdrawal?',
    body: 'The held amount goes straight back to the wallet. If a transfer is already in flight with the provider, failing it here can pay the owner twice.',
  },
};

const actionsFor = (p: WalletPayout): WalletPayoutAction[] =>
  p.status === 'pending'
    ? ['retry', 'fail']
    : p.status === 'processing'
      ? ['complete', 'fail']
      : [];

export const WalletWithdrawals = ({
  canAct,
  wid,
  scope = 'wallet',
}: {
  canAct: boolean;
  wid?: number;
  /** wallet = owner withdrawals (actionable); store rows are read-only here. */
  scope?: 'wallet' | 'store' | 'all';
}) => {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<{
    payout: WalletPayout;
    action: WalletPayoutAction;
  } | null>(null);
  const [reason, setReason] = useState('');

  const { data, isLoading } = useWalletPayoutsQuery({
    ...(status ? { status } : {}),
    ...(wid ? { wid } : {}),
    scope,
    limit: LIMIT,
    offset: (page - 1) * LIMIT,
  });
  const mutation = useWalletPayoutActionMutation();
  const cols = canAct ? 8 : 7;

  const confirm = async () => {
    if (!pending) return;
    if (pending.action === 'fail' && !reason.trim()) {
      toast.error('A reason is required', { richColors: true });
      return;
    }
    try {
      await mutation.mutateAsync({
        poid: pending.payout.poid,
        action: pending.action,
        reason: reason.trim() || undefined,
      });
      toast.success('Withdrawal updated', { richColors: true });
      setPending(null);
      setReason('');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update withdrawal', {
        richColors: true,
      });
    }
  };

  return (
    <div className={card}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold text-gray-800">
          {wid
            ? `Withdrawals from wallet #${wid}`
            : scope === 'store'
              ? 'Store payouts'
              : scope === 'all'
                ? 'All payouts'
                : 'Wallet withdrawals'}
        </h2>
        <select
          aria-label="Withdrawal status"
          className={selectCls}
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_CLS).map((s) => (
            <option key={s} value={s}>
              {s[0].toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100">
          <thead>
            <tr className="bg-gray-50">
              {[
                'ID',
                'Owner',
                'Amount',
                'Net',
                'Method',
                'Status',
                'Requested',
                ...(canAct ? [''] : []),
              ].map((h, i) => (
                <th key={i} className={thCls}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading && <SkeletonRows cols={cols} />}
            {!isLoading && !data?.payouts?.length && (
              <tr>
                <td colSpan={cols} className="py-12 text-center text-sm text-gray-500">
                  {scope === 'store' ? 'No store payouts' : scope === 'all' ? 'No payouts' : 'No withdrawals'}
                </td>
              </tr>
            )}
            {!isLoading &&
              data?.payouts?.map((p) => (
                <tr key={p.poid}>
                  <td className="px-5 py-3 font-mono text-sm">{p.poid}</td>
                  <td className="px-5 py-3 text-sm">
                    <Owner row={p} />
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold tabular-nums">
                    {formatMoney(p.amount, p.ccode)}
                  </td>
                  <td className="px-5 py-3 text-sm tabular-nums">
                    {formatMoney(p.net_amount, p.ccode)}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    {p.provider}
                    {p.provider_reference && (
                      <span className="block font-mono text-xs text-gray-500">
                        {p.provider_reference}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[p.status] ?? ''}`}
                    >
                      {p.status}
                    </span>
                    {p.failure_reason && (
                      <span
                        className="mt-1 block max-w-[220px] truncate text-xs text-gray-500"
                        title={p.failure_reason}
                      >
                        {p.failure_reason}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">{fmtDate(p.date_requested)}</td>
                  {canAct && (
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        {p.storeid && (
                          <Link
                            to={`/admin/stores/${p.storeid}`}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Manage in store
                          </Link>
                        )}
                        {(p.storeid ? [] : actionsFor(p)).map((a) => (
                          <Button
                            key={a}
                            type="button"
                            size="sm"
                            variant={a === 'fail' ? 'outline' : 'default'}
                            onClick={() => setPending({ payout: p, action: a })}
                          >
                            {ACTION_COPY[a].label}
                          </Button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-gray-100 px-5 py-3">
        <Pagination
          total={data?.pagination?.total ?? 0}
          page={page}
          itemsPerPage={LIMIT}
          limit={LIMIT}
          setPage={setPage}
          showButtons
          showSelect={false}
        />
      </div>

      <AlertDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
            setReason('');
          }
        }}
      >
        <AlertDialogContent>
          {pending && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{ACTION_COPY[pending.action].title}</AlertDialogTitle>
                <AlertDialogDescription>
                  Withdrawal #{pending.payout.poid} for{' '}
                  {formatMoney(pending.payout.amount, pending.payout.ccode)} via{' '}
                  {pending.payout.provider}. {ACTION_COPY[pending.action].body}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {pending.action === 'fail' && (
                <div className="space-y-1.5">
                  <Label htmlFor="payout-fail-reason">Reason</Label>
                  <Input
                    id="payout-fail-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this withdrawal being failed?"
                  />
                </div>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={mutation.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    void confirm();
                  }}
                >
                  {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {ACTION_COPY[pending.action].label}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ─── Feature switches ─────────────────────────────────────────────────────────

export interface FlagSwitch {
  name: string;
  label: string;
  /** Parent flag whose "off" vetoes this one. */
  master?: string;
  /** Asked before turning the switch ON — for switches that move real money. */
  confirmOn?: string;
}

const WALLET_FLAGS: FlagSwitch[] = [
  { name: 'wallet', label: 'Wallet (master switch)' },
  { name: 'wallet.fund', label: 'Add funds', master: 'wallet' },
  { name: 'wallet.pay', label: 'Pay invoices from wallet', master: 'wallet' },
  { name: 'wallet.withdraw', label: 'Withdraw', master: 'wallet' },
];

export const FlagSwitches = ({ title, flags }: { title: string; flags: FlagSwitch[] }) => {
  const { data, isLoading, error } = useFeatureFlagListQuery();
  const mutation = useUpdateFeatureFlagMutation();
  const byName = new Map((data?.flags ?? []).map((f) => [f.name, f]));

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error || !data) {
    return (
      <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        These switches need the{' '}
        <code className="rounded bg-white px-1.5 py-0.5 text-xs">system.feature_flags.manage</code>{' '}
        permission.
      </div>
    );
  }

  return (
    <div className={card}>
      <div className="border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        <p className="mt-0.5 text-sm text-gray-500">
          Changes reach every server within about a minute, with no deploy. Staged rollouts and
          history are on the{' '}
          <Link to="/admin/features/flags" className="text-blue-600 hover:underline">
            feature flags page
          </Link>
          .
        </p>
      </div>
      <ul className="divide-y divide-gray-50">
        {flags.map(({ name, label, master, confirmOn }) => {
          const flag = byName.get(name);
          if (!flag) {
            return (
              <li key={name} className="px-5 py-3 text-sm text-gray-500">
                {label}: <code className="text-xs">{name}</code> is not declared on this server yet.
              </li>
            );
          }
          // `enabled` is resolved through the parent: with the master off every
          // leaf reads off, and flipping one would overwrite its real setting.
          const lockedByMaster = !!master && byName.get(master)?.enabled === false;
          const saving = mutation.isPending && mutation.variables?.name === name;
          return (
            <li key={name} className="flex items-center justify-between gap-4 px-5 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                <p className="text-sm text-gray-500">
                  {lockedByMaster ? 'Off because the master switch is off.' : flag.description}
                </p>
                {flag.conditions && (
                  <p className="mt-0.5 text-xs text-amber-700">
                    Limited rollout: conditions apply, so not everyone has this.
                  </p>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                variant={flag.enabled ? 'default' : 'outline'}
                role="switch"
                aria-checked={flag.enabled}
                aria-label={label}
                disabled={lockedByMaster || mutation.isPending}
                onClick={() => {
                  if (!flag.enabled && confirmOn && !window.confirm(confirmOn)) return;
                  mutation.mutate(
                    {
                      name,
                      entry: flagToEntry(flag, { enabled: !flag.enabled }),
                    },
                    {
                      onSuccess: () =>
                        toast.success(`${label} turned ${flag.enabled ? 'off' : 'on'}`, {
                          richColors: true,
                        }),
                      onError: (e: any) =>
                        toast.error(e?.message || 'Failed to update switch', {
                          richColors: true,
                        }),
                    },
                  );
                }}
              >
                {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {flag.enabled ? 'On' : 'Off'}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const WalletFeatureSwitches = () => (
  <FlagSwitches title="Feature switches" flags={WALLET_FLAGS} />
);
